export type StoryVariant = 'mobile' | 'desktop'
export type StoryFormat = 'avif' | 'webp'

export const FRAME_COUNT = 180
/** The frames are served as immutable (next.config.ts): bump this whenever they are re-rendered or re-encoded. */
export const STORY_FRAME_VERSION = 4
export const MOBILE_STORY_QUERY = '(max-width: 767px)'

export const storyFrameUrl = (variant: StoryVariant, format: StoryFormat, index: number) =>
  `/landing/story/${variant}/f_${String(index).padStart(3, '0')}.${format}?v=${STORY_FRAME_VERSION}`

const range = (start: number, end: number) => Array.from({ length: end - start }, (_, i) => start + i)

/** The automatic opening plays up to about frame 19. */
const OPENING_FRAMES = 25

/**
 * Coarse-to-fine download levels: every 8th frame and the last one, then every 4th,
 * every 2nd and the rest. With the first level in, a frame at most four steps away
 * exists anywhere in the story.
 */
export const REFINEMENT_LEVELS: readonly number[][] = [8, 4, 2, 1].reduce<number[][]>((levels, step) => {
  const seen = new Set(levels.flat())
  const level = range(0, FRAME_COUNT).filter(i => i % step === 0 && !seen.has(i))
  if (step === 8) level.push(FRAME_COUNT - 1)
  return [...levels, level]
}, [])

/** The loader waits only for these; the remaining frames stream in behind the story. */
export const CRITICAL_FRAMES: readonly number[] = range(0, OPENING_FRAMES)
const criticalFrames = new Set(CRITICAL_FRAMES)
export const isCriticalFrame = (index: number) => criticalFrames.has(index)

/**
 * The frame to draw for `target`: the target itself once decoded, otherwise the decoded
 * frame closest to it between the displayed frame (`last`) and the target, else `last`.
 * Choosing any nearby frame after every decode, including one past the target, can
 * visibly jump back and forth while frames are still streaming in.
 */
export function pickShownFrame(decoded: ReadonlyMap<number, unknown>, last: number, target: number) {
  if (decoded.has(target)) return target
  let best = last
  for (const index of decoded.keys()) {
    const onTheWay = last < 0 || (index - last) * (index - target) < 0
    if (onTheWay && (best < 0 || Math.abs(index - target) < Math.abs(best - target))) best = index
  }
  return best
}

// A 1×1 AVIF: decoding it tells whether createImageBitmap handles AVIF (Safari before 16.4 does not).
const AVIF_PROBE = 'AAAAHGZ0eXBhdmlmAAAAAG1pZjFhdmlmbWlhZgAAANZtZXRhAAAAAAAAACFoZGxyAAAAAAAAAABwaWN0AAAAAAAAAAAAAAAAAAAAAA5waXRtAAAAAAABAAAAImlsb2MAAAAAREAAAQABAAAAAAD6AAEAAAAAAAAAGwAAACNpaW5mAAAAAAABAAAAFWluZmUCAAAAAAEAAGF2MDEAAAAAVmlwcnAAAAA4aXBjbwAAAAxhdjFDgSACAAAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAABZpcG1hAAAAAAAAAAEAAQOBAgMAAAAjbWRhdBIACgc4AA5QENBpMg4YAAAAQAAAsBNRQE2rWA=='
let storyFormat: Promise<StoryFormat> | undefined

export function detectStoryFormat() {
  storyFormat ??= (async (): Promise<StoryFormat> => {
    try {
      const bytes = Uint8Array.from(atob(AVIF_PROBE), character => character.charCodeAt(0))
      ;(await createImageBitmap(new Blob([bytes], { type: 'image/avif' }))).close()
      return 'avif'
    } catch {
      return 'webp'
    }
  })()
  return storyFormat
}

/**
 * Save-Data starts with the still scenes; the visitor can still turn motion on. The
 * connection's effectiveType is deliberately ignored: Chrome estimates it from response
 * times, and slow responses alone made it report "3g" on a local development server.
 */
export function prefersStillStory() {
  return Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData)
}
